import React, { useCallback, useEffect, useState } from 'react';
import { BASE } from '../services/api';
import { useToast } from '../context/ToastContext';
import '../assets/css/GestaoClientesApp.css';

/* Contas do app do cliente (proprietários e compradores): diretoria e administração
 * consultam, corrigem cadastro, redefinem senha, desativam e excluem. As rotas ficam em
 * /portal-admin e conferem a permissão no banco — esconder o menu não é a proteção. */

const FILTROS = [
  ['', 'Todos'], ['ativou', 'Já usam o app'], ['pendente', 'Não ativaram'],
  ['sem_telefone', 'Sem telefone'], ['desativado', 'Desativados'], ['exclusao', 'Pediram exclusão'],
];

const telefoneBR = (t) => {
  if (!t) return '—';
  const n = String(t).replace(/^55/, '');
  return n.length >= 10 ? `(${n.slice(0, 2)}) ${n.slice(2, -4)}-${n.slice(-4)}` : t;
};
const documentoBR = (d) => {
  if (!d) return '—';
  if (d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length === 14) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  return d;
};
const dataBR = (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—');

function situacao(c) {
  if (!c.ativo) return ['Desativado', 'is-off'];
  if (c.exclusao_solicitada_em) return ['Pediu exclusão', 'is-alerta'];
  if (!c.telefone) return ['Sem telefone', 'is-alerta'];
  return c.ativou ? ['Usa o app', 'is-ok'] : ['Não ativou', 'is-pendente'];
}

async function chamar(caminho, { method = 'GET', body } = {}) {
  const r = await fetch(`${BASE}/portal-admin${caminho}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Não foi possível concluir.');
  return d;
}

export default function GestaoClientesApp() {
  const toast = useToast();
  const [busca, setBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  const [filtro, setFiltro] = useState('');
  const [pagina, setPagina] = useState(1);
  const [lista, setLista] = useState({ itens: [], total: 0, paginas: 1 });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aberto, setAberto] = useState(null);      // id da ficha aberta
  const [novo, setNovo] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const qs = new URLSearchParams({ pagina: String(pagina) });
      if (buscaAtiva) qs.set('busca', buscaAtiva);
      if (filtro) qs.set('filtro', filtro);
      setLista(await chamar(`/clientes?${qs}`));
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [pagina, buscaAtiva, filtro]);

  useEffect(() => { carregar(); }, [carregar]);

  const buscar = (e) => { e.preventDefault(); setPagina(1); setBuscaAtiva(busca.trim()); };

  return (
    <div className="ca-page">
      <header className="ca-hero">
        <div>
          <span className="ca-eyebrow"><i />Administração</span>
          <h1>Clientes do App</h1>
          <p>Proprietários e compradores com conta no app do cliente. O login é o telefone; a senha
            quem cria é o próprio cliente — redefinir apaga a senha e ele cria outra no app.</p>
        </div>
        <button type="button" className="ca-cta" onClick={() => setNovo(true)}>Novo cliente</button>
      </header>

      <form className="ca-busca" onSubmit={buscar}>
        <input value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar cliente"
          placeholder="Nome, telefone, CPF, e-mail ou código do imóvel" />
        <button type="submit" className="ca-cta">Buscar</button>
        {buscaAtiva && (
          <button type="button" className="ca-sec" onClick={() => { setBusca(''); setBuscaAtiva(''); setPagina(1); }}>Limpar</button>
        )}
        <span className="ca-contador">{carregando ? 'Carregando…' : `${lista.total.toLocaleString('pt-BR')} cliente(s)`}</span>
      </form>

      <div className="ca-chips" role="tablist">
        {FILTROS.map(([valor, rotulo]) => (
          <button key={valor || 'todos'} type="button" role="tab" aria-selected={filtro === valor}
            className={filtro === valor ? 'is-ativo' : ''} onClick={() => { setFiltro(valor); setPagina(1); }}>
            {rotulo}
          </button>
        ))}
      </div>

      {erro && <div className="ca-erro" role="alert">{erro}</div>}

      <div className="ca-tabela-wrap">
        <table className="ca-tabela">
          <thead>
            <tr><th>Nome</th><th>Telefone (login)</th><th>CPF/CNPJ</th><th>E-mail</th><th>Imóveis</th><th>Situação</th></tr>
          </thead>
          <tbody>
            {lista.itens.map((c) => {
              const [rotulo, classe] = situacao(c);
              return (
                <tr key={c.id} onClick={() => setAberto(c.id)} tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') setAberto(c.id); }}>
                  <td className="ca-nome">{c.nome}</td>
                  <td>{telefoneBR(c.telefone)}</td>
                  <td>{documentoBR(c.cpf)}</td>
                  <td className="ca-email">{c.email || '—'}</td>
                  <td>{c.imoveis}</td>
                  <td><span className={`ca-sit ${classe}`}>{rotulo}</span></td>
                </tr>
              );
            })}
            {!carregando && lista.itens.length === 0 && (
              <tr><td colSpan={6} className="ca-vazio">Nenhum cliente encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {lista.paginas > 1 && (
        <div className="ca-paginacao">
          <button type="button" className="ca-sec" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>Anterior</button>
          <span>Página {pagina} de {lista.paginas}</span>
          <button type="button" className="ca-sec" disabled={pagina >= lista.paginas} onClick={() => setPagina(pagina + 1)}>Próxima</button>
        </div>
      )}

      {aberto && (
        <FichaCliente id={aberto} toast={toast} onFechar={() => setAberto(null)}
          onMudou={carregar} onExcluido={() => { setAberto(null); carregar(); }} />
      )}
      {novo && (
        <NovoCliente toast={toast} onFechar={() => setNovo(false)}
          onCriado={(c) => { setNovo(false); carregar(); setAberto(c.id); }} />
      )}
    </div>
  );
}

function FichaCliente({ id, toast, onFechar, onMudou, onExcluido }) {
  const [ficha, setFicha] = useState(null);
  const [form, setForm] = useState({ nome: '', telefone: '', cpf: '', email: '' });
  const [codigo, setCodigo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');

  const aplicar = (f) => {
    setFicha(f);
    setForm({ nome: f.nome || '', telefone: telefoneBR(f.telefone).replace('—', ''), cpf: f.cpf || '', email: f.email || '' });
  };

  useEffect(() => {
    let vivo = true;
    chamar(`/clientes/${id}`).then((f) => { if (vivo) aplicar(f); }).catch((e) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [id]);

  async function executar(acao, sucesso) {
    setOcupado(true); setErro('');
    try {
      const r = await acao();
      if (r && r.id) aplicar(r);
      toast(sucesso, 'success');
      onMudou();
      return r;
    } catch (e) {
      setErro(e.message);
      return null;
    } finally {
      setOcupado(false);
    }
  }

  const salvar = (e) => {
    e.preventDefault();
    const mudancas = {};
    if (form.nome.trim() !== (ficha.nome || '')) mudancas.nome = form.nome.trim();
    const telDigitos = form.telefone.replace(/\D/g, '');
    if (telDigitos !== String(ficha.telefone || '').replace(/^55/, '')) mudancas.telefone = form.telefone.trim() || null;
    if (form.cpf.replace(/\D/g, '') !== (ficha.cpf || '')) mudancas.cpf = form.cpf.trim() || null;
    if (form.email.trim().toLowerCase() !== (ficha.email || '')) mudancas.email = form.email.trim() || null;
    if (!Object.keys(mudancas).length) { toast('Nada mudou.', 'info'); return; }
    if (mudancas.telefone !== undefined && ficha.ativou
        && !window.confirm('Trocar o telefone muda o login do cliente e encerra as sessões abertas. Continuar?')) return;
    executar(() => chamar(`/clientes/${id}`, { method: 'PATCH', body: mudancas }), 'Cadastro atualizado.');
  };

  const redefinir = () => {
    if (!window.confirm(`Apagar a senha de ${ficha.nome}? No próximo acesso ele digita o telefone e cria uma nova.`)) return;
    executar(() => chamar(`/clientes/${id}/redefinir-senha`, { method: 'POST' }), 'Senha apagada: o cliente cria outra no app.');
  };

  const alternarAtivo = () => {
    const desativar = ficha.ativo;
    if (desativar && !window.confirm(`Desativar o acesso de ${ficha.nome}? Ele sai do app na hora.`)) return;
    executar(() => chamar(`/clientes/${id}`, { method: 'PATCH', body: { ativo: !desativar } }),
      desativar ? 'Acesso desativado.' : 'Acesso reativado.');
  };

  const excluir = async () => {
    const confirmacao = window.prompt(
      `Excluir DEFINITIVAMENTE a conta de ${ficha.nome}? Visitas e propostas continuam no sistema.\n`
      + 'Se ele ainda tiver imóvel à venda no Imoview, a sincronização cria a conta de novo — para barrar o acesso, desative.\n\n'
      + 'Digite EXCLUIR para confirmar.');
    if (confirmacao !== 'EXCLUIR') return;
    const r = await executar(() => chamar(`/clientes/${id}`, { method: 'DELETE' }), 'Conta excluída.');
    if (r) onExcluido();
  };

  const vincular = (e) => {
    e.preventDefault();
    if (!codigo.trim()) return;
    executar(() => chamar(`/clientes/${id}/imoveis`, { method: 'POST', body: { codigo_imovel: codigo.trim() } }),
      `Imóvel ${codigo.trim()} vinculado.`).then((r) => { if (r) setCodigo(''); });
  };

  const desvincular = (cod) => {
    if (!window.confirm(`Tirar o imóvel ${cod} da conta? Se o Imoview ainda o liga a este proprietário, a sincronização vincula de novo.`)) return;
    executar(() => chamar(`/clientes/${id}/imoveis/${encodeURIComponent(cod)}`, { method: 'DELETE' }), `Imóvel ${cod} desvinculado.`);
  };

  return (
    <div className="ca-fundo" role="presentation" onClick={onFechar}>
      <aside className="ca-ficha" role="dialog" aria-modal="true" aria-label="Ficha do cliente" onClick={(e) => e.stopPropagation()}>
        <div className="ca-ficha-topo">
          <div>
            <span className="ca-eyebrow"><i />Cliente #{id}</span>
            <h2>{ficha ? ficha.nome : 'Carregando…'}</h2>
          </div>
          <button type="button" className="ca-fechar" onClick={onFechar} aria-label="Fechar">×</button>
        </div>
        {erro && <div className="ca-erro" role="alert">{erro}</div>}
        {ficha && (
          <>
            <div className="ca-selos">
              <span className={`ca-sit ${situacao(ficha)[1]}`}>{situacao(ficha)[0]}</span>
              {ficha.vinculos.length > 0 && <span className="ca-sit is-neutro">Proprietário</span>}
              {ficha.comprador && <span className="ca-sit is-neutro">Comprador</span>}
            </div>
            <dl className="ca-dados">
              <div><dt>Ativou o app em</dt><dd>{dataBR(ficha.ativado_em)}</dd></div>
              <div><dt>Pedido de exclusão</dt><dd>{ficha.exclusao_solicitada_em
                ? `${dataBR(ficha.exclusao_solicitada_em)}${ficha.exclusao_motivo ? ` — “${ficha.exclusao_motivo}”` : ''}` : '—'}</dd></div>
            </dl>

            <form className="ca-form" onSubmit={salvar}>
              <h3>Cadastro</h3>
              <label><span>Nome</span>
                <input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} maxLength={160} required />
              </label>
              <label><span>Telefone (é o login)</span>
                <input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} placeholder="(61) 99999-9999" />
              </label>
              <label><span>CPF/CNPJ</span>
                <input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="Opcional" />
              </label>
              <label><span>E-mail</span>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Opcional" />
              </label>
              <button type="submit" className="ca-cta" disabled={ocupado}>{ocupado ? 'Salvando…' : 'Salvar cadastro'}</button>
            </form>

            <section className="ca-bloco">
              <h3>Imóveis à venda nesta conta</h3>
              {ficha.vinculos.length ? (
                <ul className="ca-imoveis">
                  {ficha.vinculos.map((v) => (
                    <li key={v.codigo_imovel}>
                      <strong>{v.codigo_imovel}</strong>
                      <button type="button" className="ca-link-perigo" disabled={ocupado} onClick={() => desvincular(v.codigo_imovel)}>Desvincular</button>
                    </li>
                  ))}
                </ul>
              ) : <p className="ca-nota">Nenhum imóvel vinculado{ficha.comprador ? ' — entra só como comprador.' : '.'}</p>}
              <form className="ca-linha" onSubmit={vincular}>
                <input value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="Código do imóvel" aria-label="Código do imóvel" />
                <button type="submit" className="ca-sec" disabled={ocupado || !codigo.trim()}>Vincular</button>
              </form>
            </section>

            <section className="ca-bloco">
              <h3>Acesso</h3>
              <div className="ca-acoes">
                <button type="button" className="ca-sec" disabled={ocupado || !ficha.ativou} onClick={redefinir}
                  title={ficha.ativou ? '' : 'Ainda não criou senha'}>Redefinir senha</button>
                <button type="button" className="ca-sec" disabled={ocupado} onClick={alternarAtivo}>
                  {ficha.ativo ? 'Desativar acesso' : 'Reativar acesso'}
                </button>
                <button type="button" className="ca-perigo" disabled={ocupado} onClick={excluir}>Excluir conta</button>
              </div>
              <p className="ca-nota">Redefinir apaga a senha: ninguém da equipe fica sabendo a nova. Desativar e trocar o
                telefone encerram as sessões abertas do cliente.</p>
            </section>
          </>
        )}
      </aside>
    </div>
  );
}

function NovoCliente({ toast, onFechar, onCriado }) {
  const [form, setForm] = useState({ nome: '', telefone: '', cpf: '', email: '', imoveis: '' });
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');

  const criar = async (e) => {
    e.preventDefault();
    setOcupado(true); setErro('');
    try {
      const corpo = { nome: form.nome.trim(), telefone: form.telefone.trim(),
        imoveis: form.imoveis.split(/[\s,;]+/).filter(Boolean) };
      if (form.cpf.trim()) corpo.cpf = form.cpf.trim();
      if (form.email.trim()) corpo.email = form.email.trim();
      const c = await chamar('/clientes', { method: 'POST', body: corpo });
      toast('Cliente criado. Ele entra no app pelo telefone e cria a senha.', 'success');
      onCriado(c);
    } catch (err) {
      setErro(err.message);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="ca-fundo" role="presentation" onClick={onFechar}>
      <aside className="ca-ficha" role="dialog" aria-modal="true" aria-label="Novo cliente" onClick={(e) => e.stopPropagation()}>
        <div className="ca-ficha-topo">
          <div><span className="ca-eyebrow"><i />Novo</span><h2>Cliente do app</h2></div>
          <button type="button" className="ca-fechar" onClick={onFechar} aria-label="Fechar">×</button>
        </div>
        <p className="ca-nota">Use quando a sincronização com o Imoview não trouxe o proprietário (ex.: telefone
          diferente no cadastro). A conta nasce sem senha.</p>
        {erro && <div className="ca-erro" role="alert">{erro}</div>}
        <form className="ca-form" onSubmit={criar}>
          <label><span>Nome</span><input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required maxLength={160} /></label>
          <label><span>Telefone (login)</span><input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} required placeholder="(61) 99999-9999" /></label>
          <label><span>CPF/CNPJ</span><input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="Opcional" /></label>
          <label><span>E-mail</span><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Opcional" /></label>
          <label><span>Imóveis (códigos)</span><input value={form.imoveis} onChange={(e) => setForm({ ...form, imoveis: e.target.value })} placeholder="Ex.: 10770, 3123" /></label>
          <button type="submit" className="ca-cta" disabled={ocupado}>{ocupado ? 'Criando…' : 'Criar cliente'}</button>
        </form>
      </aside>
    </div>
  );
}
